import {
  Button,
  cn,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@bleu.builders/ui";
import { MinusIcon, PlusIcon, UpdateIcon } from "@radix-ui/react-icons";
import { Calculator, TriangleAlert } from "lucide-react";
import React from "react";
import { Link } from "react-router-dom";

import { DownloadThesisKit } from "./DownloadThesisKit";
import { DownloadUpdatedThesisModal } from "./DownloadUpdatedThesisModal";
import { ThesisRelatedNewsArticles } from "./ThesisRelatedNewsArticles";
import { ThesisArchivedNote } from "./ThesisTableArchivedNotification";

export function ThesisDetailsActions({ row }) {
  return (
    <div className="flex flex-row space-x-4 items-center justify-end w-full text-sm">
      <Link
        to={`/simulate-calculation/${row.original.calculator_id}`}
        target="_blank"
      >
        <Button
          className={cn("gap-2", {
            "bg-muted-foreground hover:bg-muted-foreground/90":
              row.original.archived,
          })}
        >
          <Calculator /> Simular Cálculo
        </Button>
      </Link>
      <DownloadThesisKit
        thesis={row.original}
        buttonClassName={{
          "bg-muted-foreground hover:bg-muted-foreground/90":
            row.original.archived,
        }}
      />
    </div>
  );
}

export function ThesisContent({ title, children, defaultOpen = false }) {
  const [isExpanded, setIsExpanded] = React.useState(defaultOpen);

  if (!children) return null;

  return (
    <Collapsible
      open={isExpanded}
      onOpenChange={setIsExpanded}
      className="space-y-2"
    >
      <CollapsibleTrigger
        className={cn(
          "w-full flex items-center justify-start gap-1 font-semibold px-2 select-text",
          {
            "text-primary": isExpanded,
          },
        )}
      >
        {!isExpanded ? (
          <PlusIcon className="text-muted-foreground size-4 font-bold" />
        ) : (
          <MinusIcon className="text-muted-foreground size-4 font-bold" />
        )}
        <span className="uppercase">{title}</span>
      </CollapsibleTrigger>
      <CollapsibleContent className="mx-7">
        <div className="prose max-w-none prose-a:text-blue-600 hover:prose-a:text-blue-500">
          {children}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

export function ThesisDetails({ row, defaultOpen }) {
  return (
    <div className="px-2 bg-muted py-4 flex justify-start flex-col items-start rounded-b-md space-y-4">
      {row.original.archived && (
        <ThesisArchivedNote reason={row.original.archived_reason} />
      )}
      <ThesisContent defaultOpen title="O que está sendo discutido">
        <WysiwygText text={row.original.objective} />
      </ThesisContent>
      <ThesisContent defaultOpen={defaultOpen} title="Requisitos da tese">
        <WysiwygText text={row.original.comments} />
      </ThesisContent>
      <ThesisContent defaultOpen={defaultOpen} title="Resumo da tese">
        <WysiwygText text={row.original.summary} />
      </ThesisContent>
      <ThesisContent defaultOpen={defaultOpen} title="Período recuperável">
        <WysiwygText text={row.original.recoverable_period} />
      </ThesisContent>
      <ThesisContent defaultOpen={defaultOpen} title="Valor a recuperar">
        <WysiwygText text={row.original.recoverable_value} />
      </ThesisContent>
      <ThesisContent defaultOpen={defaultOpen} title="Panorama jurisprudencial">
        <WysiwygText text={row.original.decisions_text} />
      </ThesisContent>
      <ThesisContent defaultOpen title="Últimas Notícias">
        <ThesisRelatedNewsArticles thesis_id={row.original.id} />
      </ThesisContent>

      <div className="flex flex-col ml-auto mr-7 justify-end gap-2">
        {row.original.has_update && (
          <DownloadUpdatedThesisModal
            thesisId={row.original.id}
            thesisTitle={row.original.title}
            optToDownloadOldKit={false}
          >
            <a role="button" className="flex gap-2 justify-end items-center">
              <UpdateIcon className="size-4 text-green-600 font-bold" />
              <span className="text-primary underline mr-1">
                Atualização disponivel
              </span>
            </a>
          </DownloadUpdatedThesisModal>
        )}
        <ThesisDetailsActions row={row} />
      </div>
    </div>
  );
}

function WysiwygText({ text }) {
  return <div dangerouslySetInnerHTML={{ __html: text }}></div>;
}
